import type { RbacPlugin } from '@twinepos/vue-rbac'

import { router } from '@inertiajs/vue3'
import { createRbac } from '@twinepos/vue-rbac'
import { createForbiddenRevalidator, createHttpTransport } from '@twinepos/vue-rbac-http'
import { shallowRef } from 'vue'

import type { AbilitiesIdentity, AbilitiesIdentityProps } from '@/domains/rbac/rbac.types'

import { ApiClient, AUTH_TOKEN_STORAGE_KEY } from '@/services/api/apiClient'
import { queryClient } from '@/support/query/queryClient'

/**
 * The app's RBAC instance. The backend (`GET /admin/me/abilities`) is the
 * authority; this renders UI conditionally as a UX affordance, and the 403 stays
 * the real boundary.
 *
 * Auth: the endpoint sits behind the admin stack (`auth:sanctum,landlord`). We
 * send both the session cookie (`credentials: include`, handled by the transport)
 * and the SPA bearer token the rest of the API uses, so it authenticates either
 * way.
 */

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** Whose abilities a payload names. Both fields stay empty for a guest. */
const readIdentity = (props: AbilitiesIdentityProps): AbilitiesIdentity => ({
  tenantId: props.tenant?.id,
  userId: props.auth?.user?.id
})

const sameIdentity = (left: AbilitiesIdentity, right: AbilitiesIdentity): boolean =>
  left.tenantId === right.tenantId && left.userId === right.userId

/** The shared props that carry the principal. */
const IDENTITY_PROPS = ['auth', 'tenant'] as const

/**
 * Whether a payload says anything about the principal at all. A payload missing
 * either prop is not describing a guest — it is not describing the principal.
 *
 * An Inertia partial reload (`router.reload({ only: [...] })`) returns only the
 * props it asked for. Reading an absent `auth` as "signed out" would clear the
 * held rules on every one of them and hide each gated affordance until a reload —
 * the exact failure this wiring exists to prevent. The client does merge partial
 * props into the page, but a permission gate should not rest on that internal.
 *
 * The keys are checked with `in` rather than for a value: the props type makes
 * both optional, so absent and empty are the same type and only presence
 * separates them.
 */
const describesPrincipal = (props: AbilitiesIdentityProps): boolean => IDENTITY_PROPS.every(prop => prop in props)

/**
 * The bootstrap props are passed IN, and must not come from `usePage()`.
 *
 * `usePage()` is unusable at this point in the lifecycle: Inertia assigns its
 * page ref inside the `App` component's own `setup()`, which runs during
 * `app.mount()` — whereas `app.use(...)` installs before that and this transport
 * starts synchronously on install. Reading `usePage().props` there dereferences
 * `undefined`. `app.ts` reads the bootstrap props instead, where the same values
 * are already available and already typed.
 *
 * They seed the identity rather than fixing it. Signing in is an Inertia visit,
 * not a page load, so this factory does not re-run for it — the same reason
 * `installCallout` tracks navigation. Held fixed, the cache key kept naming the
 * guest who opened the login page, whose abilities fetch the admin auth stack
 * refused, so every gated section stayed hidden for the whole session. Only a
 * manual reload re-ran this and fixed it.
 */
export const createAppRbac = (bootstrapProps: AbilitiesIdentityProps): RbacPlugin => {
  const identity = shallowRef<AbilitiesIdentity>(readIdentity(bootstrapProps))

  /**
   * Whose abilities these are, mirroring the payload's own `tenant_id` +
   * `subject`. Both belong in the cache key: entries live for the session, so a
   * key missing either one would serve the previous principal's rules after a
   * tenant switch or a re-login in the same tab.
   *
   * Read reactively by the transport, which re-points at the new key when this
   * changes.
   */
  const abilitiesCacheKey = (): (string | number)[] => [
    identity.value.tenantId ?? 'no-tenant',
    identity.value.userId ?? 'self'
  ]

  /**
   * A guest holds no abilities: the admin auth stack refuses their fetch with a
   * 401. So the transport waits for the sign-in visit rather than asking.
   */
  const isSignedIn = (): boolean => identity.value.userId !== undefined

  const rbac = createRbac({
    transport: createHttpTransport({
      endpoint: '/admin/me/abilities',
      queryClient,
      cacheKey: abilitiesCacheKey,
      isSignedIn,
      headers: authHeaders
    })
  })

  // The abilities we hold can go stale between revalidations — a role edited, a
  // permission revoked, an impersonation started. The backend does not announce
  // that (no drift header, deliberately: the ETag is the revalidation mechanism),
  // so the signal we use is the boundary itself. Any 401/403 means the server
  // disagreed with an affordance we rendered; refetch and let the ETag make it a
  // cheap 304 when nothing actually changed.
  //
  // It deliberately stays quiet until a first payload has landed, so it cannot
  // recover a principal whose own fetch never succeeded. Re-keying below is what
  // covers that.
  ApiClient.onResponse(createForbiddenRevalidator(rbac.controller))

  // Signing in, signing out, and switching to a different user in the same tab
  // are all Inertia visits, so this is the only point at which the app learns the
  // principal changed.
  router.on('success', event => {
    const props = event.detail.page.props as AbilitiesIdentityProps

    if (!describesPrincipal(props)) {
      return
    }

    const next = readIdentity(props)

    if (sameIdentity(identity.value, next)) {
      return
    }

    identity.value = next
  })

  return rbac
}
